import { Dialog, Box, Typography, TextField, Button, DialogContent, FormControl, Autocomplete, CircularProgress } from "@mui/material";
import { useMutation } from "@apollo/client";
import { Mutations } from "services/GraphQL/Mutations";
import { useNotifications } from "contexts/Notification";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";
import { useState, useCallback } from "react";
import useAlgoliaSearchClient from "hooks/useAlgoliaSearchClient";
import { useSettings } from "contexts/Settings";
import { InstantSearch } from "react-instantsearch-core";
import { Configure, useHits, useSearchBox } from "react-instantsearch";
import moment from "moment";
import { Case } from "services/GraphQl";

interface IAdminCreateUserDialogInput {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  zipCode: string;
}

interface ICaseUserProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: string | moment.Moment;
  zipCode?: string;
}

const syncUserConfirmationMessage = "By linking this user and case, the user may access sensitive personal information about the case. In order to proceed, please confirm you have verified the identity of the user by validating 2-3 user data points (e.g., address, date of birth, etc.).";

function AdminSyncExistingUserDialog({
  open,
  onClose,
  companyId,
  caseId,
  caseUserProps,
  theCase,
}: {
  open: boolean;
  onClose: () => void;
  companyId: string;
  caseId: string;
  caseUserProps: ICaseUserProps;
  theCase: Case;
}) {
  const { handleSubmit } = useForm<IAdminCreateUserDialogInput>();

  const { settings } = useSettings();
  const ALGOLIA_INDEX = settings.REACT_APP_ALGOLIA_USERS_INDEX;
  const searchClient = useAlgoliaSearchClient(ALGOLIA_INDEX);
  const { notifications } = useNotifications();
  const navigate = useNavigate();

  const [syncDataForExitCode] = useMutation(Mutations.syncDataForExitCode);

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userUid, setUserUid] = useState<string | undefined>(undefined);

  // used for the syncDataForExitCode mutation
  const { lastName, dateOfBirth, zipCode } = caseUserProps;

  const CustomHits = () => {
    const { items } = useHits();
    const { refine: refineSearch } = useSearchBox();
    const [inputValue, setInputValue] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const debouncedSearch = useCallback(
      (value: string) => {
        refineSearch(value);
        setIsSearching(false);
      },
      [refineSearch]
    );


    return (
      <FormControl fullWidth>
        <Autocomplete
          value={selectedUser}
          inputValue={inputValue}
          onInputChange={(_, newValue) => {
            setInputValue(newValue);
            setIsSearching(true);
            setTimeout(() => {
              debouncedSearch(newValue);
            }, 1000);
          }}
          onChange={(_, newValue) => {
            setSelectedUser(newValue);
            setUserUid(newValue?.uid);
          }}
          options={items}
          getOptionLabel={(option: any) => `${option.name} - ${option.email} (${option.uid})`}
          filterOptions={(x) => x}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Search User by Name, Email, or ID" 
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            inputValue.length < 2 
              ? "Type at least 2 characters to search" 
              : "No users found"
          }
        />
      </FormControl>
    );
  };

  const onSubmit: SubmitHandler<IAdminCreateUserDialogInput> = async () => {
    try {
      try {
        const result = await syncDataForExitCode({
          variables: {
            companyId,
            exitCode: caseId,
            lastName: lastName || "",
            birthDate: moment(dateOfBirth).format("YYYY-MM-DD") || "",
            zipCode: zipCode || "",
            isAdmin: true,
            userUid: selectedUser.uid,
          },
        });
        if (!result.data?.syncDataForExitCode) {
          throw new Error("Error syncing user data");
        }
      } catch (error) {
        throw new Error("Error syncing user data");
      }

      notifications.success(`User ${selectedUser.email} synced successfully`);

      navigate(`/admin/users/${userUid}`);
    } catch (error) {
      notifications.error(`Error syncing user: ${error}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      sx={{
        "& .MuiPaper-root": {
          width: { sm: "70%", xs: "90%" },
          maxWidth: "800px",
          margin: "16px",
          overflow: "visible",
        },
      }}
    >
      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography variant="h3">Sync Existing User to Case</Typography>
          <InstantSearch indexName={ALGOLIA_INDEX} searchClient={searchClient}>
            <Configure filters="isCaseSynced:false" />
            <CustomHits />
          </InstantSearch>
          {selectedUser && (
            <Box sx={{ my: 2 }}>
              <Typography variant="h4">Sync this user to the case?</Typography>
              <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1">Case ID: {caseId}</Typography>
                  <Typography variant="body1">
                    Name: {theCase.contact?.firstName} {theCase.contact?.lastName}
                  </Typography>
                  <Typography variant="body1">
                    Event Date: {moment(theCase.event?.date).format("MM/DD/YYYY")}
                  </Typography>
                  <Typography variant="body1">Event Type: {theCase.event?.type}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1">User name: {selectedUser.name}</Typography>
                  <Typography variant="body1">User email: {selectedUser.email}</Typography>
                  <Typography variant="body1">User UID: {selectedUser.uid}</Typography>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ mt: 2 }}>{syncUserConfirmationMessage}</Typography>
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button type="submit" variant="contained" disabled={!selectedUser}>
                  Sync User to Case
                </Button>
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={() => {
                    onClose();
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AdminSyncExistingUserDialog;
